import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Mobile "tap to reveal, tap to act" pattern.
 * On mobile (<=768px): first tap expands to show label, second tap triggers action.
 * On desktop: always expanded, single click triggers action.
 * Auto-collapses after 3 seconds of inactivity.
 */
export default function useMobileExpand() {
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const collapseTimer = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const clearTimer = () => {
    if (collapseTimer.current) { clearTimeout(collapseTimer.current); collapseTimer.current = null; }
  };

  const startCollapseTimer = useCallback(() => {
    clearTimer();
    collapseTimer.current = setTimeout(() => setExpanded(false), 3000);
  }, []);

  // On mobile: first tap expands, second tap returns true (ready to act)
  // On desktop: always returns true (act immediately)
  const handleTap = useCallback(() => {
    if (!isMobile) return true; // Desktop: always act
    if (expanded) {
      clearTimer();
      setExpanded(false);
      return true; // Second tap: act
    }
    setExpanded(true);
    startCollapseTimer();
    return false; // First tap: just expand
  }, [isMobile, expanded, startCollapseTimer]);

  // Whether to show label text
  const showLabel = !isMobile || expanded;

  useEffect(() => {
    return () => clearTimer();
  }, []);

  return { isMobile, expanded, showLabel, handleTap };
}
